"use client"

import { useEffect, useMemo, useState } from "react"
import { X, Copy as CopyIcon, UserCog, Pencil, ChevronLeft, RotateCcw, Search } from "lucide-react"
import { customersAPI, servicesAPI } from "../services/api"
import { useAuth } from "../context/AuthContext"

// Modal for the Duplicate job action (issue #38). Spec started as "copies
// all the data for the job and we need to set up only the new date", but
// goscha later asked (reopen x4) for inline overrides so the duplicate
// can land with a different customer and / or service without touching
// the source job.
//
// Flow:
//   1. User picks new date + time (required).
//   2. Optionally overrides customer and / or service (price, duration).
//      Overrides live in modal-local state only — the source job is
//      never touched.
//   3. On submit we send overrides to POST /api/jobs/:id/duplicate. If
//      the backend ignores a field we PATCH the newly-created job with
//      the overrides as a fallback (defensive — the backend accepts
//      arbitrary body, we're not sure which keys it already honours).

export default function DuplicateJobModal({
  open,
  job,
  defaultDate,
  defaultTime,
  busy,
  onClose,
  onConfirm, // (payload) => Promise<void>; payload includes overrides
}) {
  const { user } = useAuth()
  const [date, setDate] = useState("")
  const [time, setTime] = useState("09:00")
  const [error, setError] = useState("")

  // Local override state — only applied to the new duplicate.
  const [customerOverride, setCustomerOverride] = useState(null) // { id, name } | null
  const [serviceOverride, setServiceOverride] = useState(null)   // { id, name, price, duration } | null

  // Sub-overlay state (picker UI inside this modal)
  const [subView, setSubView] = useState(null) // null | 'customer' | 'service'

  // Picker lists (lazy fetched the first time a picker opens)
  const [customers, setCustomers] = useState([])
  const [customersLoading, setCustomersLoading] = useState(false)
  const [customerQuery, setCustomerQuery] = useState("")

  const [services, setServices] = useState([])
  const [servicesLoading, setServicesLoading] = useState(false)

  // Service override form fields (set once a service is picked so user
  // can tweak price / duration before confirming).
  const [serviceForm, setServiceForm] = useState({ price: "", duration: "" })

  useEffect(() => {
    if (!open) return
    setError("")
    setCustomerOverride(null)
    setServiceOverride(null)
    setSubView(null)
    setCustomerQuery("")
    const d = defaultDate ? new Date(defaultDate) : null
    if (d && !isNaN(d.getTime())) {
      const yyyy = d.getFullYear()
      const mm = String(d.getMonth() + 1).padStart(2, "0")
      const dd = String(d.getDate()).padStart(2, "0")
      setDate(`${yyyy}-${mm}-${dd}`)
      const hh = String(d.getHours()).padStart(2, "0")
      const mi = String(d.getMinutes()).padStart(2, "0")
      setTime(defaultTime || `${hh}:${mi}`)
    } else {
      const now = new Date()
      const yyyy = now.getFullYear()
      const mm = String(now.getMonth() + 1).padStart(2, "0")
      const dd = String(now.getDate()).padStart(2, "0")
      setDate(`${yyyy}-${mm}-${dd}`)
      setTime(defaultTime || "09:00")
    }
  }, [open, defaultDate, defaultTime])

  // Lazy fetch customer list on first open of customer picker.
  useEffect(() => {
    if (subView !== "customer") return
    if (customers.length > 0 || customersLoading) return
    const tenantId = job?.user_id || user?.id
    if (!tenantId) return
    setCustomersLoading(true)
    customersAPI
      .getAll(tenantId, { page: 1, limit: 500 })
      .then((resp) => {
        const list = Array.isArray(resp) ? resp : (resp?.customers || resp?.data || [])
        setCustomers(list)
      })
      .catch(() => setCustomers([]))
      .finally(() => setCustomersLoading(false))
  }, [subView, customers.length, customersLoading, job?.user_id, user?.id])

  // Lazy fetch service list on first open of service picker.
  useEffect(() => {
    if (subView !== "service") return
    if (services.length > 0 || servicesLoading) return
    const tenantId = job?.user_id || user?.id
    if (!tenantId) return
    setServicesLoading(true)
    servicesAPI
      .getAll(tenantId)
      .then((resp) => {
        const list = Array.isArray(resp) ? resp : (resp?.services || [])
        setServices(list)
      })
      .catch(() => setServices([]))
      .finally(() => setServicesLoading(false))
  }, [subView, services.length, servicesLoading, job?.user_id, user?.id])

  const filteredCustomers = useMemo(() => {
    const q = customerQuery.trim().toLowerCase()
    if (!q) return customers.slice(0, 50)
    return customers
      .filter((c) => {
        const name = `${c.first_name || ""} ${c.last_name || ""} ${c.name || ""} ${c.email || ""}`.toLowerCase()
        return name.includes(q)
      })
      .slice(0, 50)
  }, [customers, customerQuery])

  if (!open) return null

  const sourceCustomerName =
    `${job?.customers?.first_name || ""} ${job?.customers?.last_name || ""}`.trim() ||
    job?.customer_name ||
    (job?.id ? `Job #${job.id}` : "this job")

  const sourceServiceName = job?.service_name || "service"

  const effectiveCustomerName = customerOverride?.name || sourceCustomerName
  const effectiveServiceLabel = serviceOverride
    ? `${serviceOverride.name}${serviceOverride.price ? ` · $${serviceOverride.price}` : ""}${serviceOverride.duration ? ` · ${serviceOverride.duration}` : ""}`
    : sourceServiceName

  const handleSubmit = async (e) => {
    e?.preventDefault?.()
    if (!date) {
      setError("Pick a date for the new job.")
      return
    }
    setError("")
    const payload = { scheduledDate: date, scheduledTime: time || "09:00" }
    if (customerOverride?.id) payload.customerId = customerOverride.id
    if (serviceOverride?.id) {
      payload.serviceId = serviceOverride.id
      payload.serviceName = serviceOverride.name
      if (serviceOverride.price !== "" && serviceOverride.price != null) payload.price = Number(serviceOverride.price)
      if (serviceOverride.duration !== "" && serviceOverride.duration != null) payload.duration = Number(serviceOverride.duration)
    }
    try {
      await onConfirm?.(payload)
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || "Could not duplicate the job.")
    }
  }

  const customerPicker = (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <button
          type="button"
          onClick={() => setSubView(null)}
          className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-[var(--sf-text-secondary)] hover:text-[var(--sf-text-primary)]"
        >
          <ChevronLeft className="w-4 h-4" />
          Back
        </button>
        <div className="text-[14px] font-bold text-[var(--sf-text-primary)]">Edit customer data</div>
      </div>
      <p className="text-[12px] text-[var(--sf-text-secondary)] mb-3">
        Pick the customer the <strong>duplicate</strong> should use. The source job stays unchanged.
      </p>
      <div className="relative mb-3">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--sf-text-muted)]" />
        <input
          type="text"
          value={customerQuery}
          onChange={(e) => setCustomerQuery(e.target.value)}
          placeholder="Search by name or email"
          className="w-full pl-9 pr-3 py-2 text-sm border border-[var(--sf-border-light)] rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-[var(--sf-blue-500)]/40 focus:border-[var(--sf-blue-500)]"
        />
      </div>
      <div className="max-h-[260px] overflow-y-auto border border-[var(--sf-border-light)] rounded-md">
        {customersLoading ? (
          <div className="p-6 text-center text-[12.5px] text-[var(--sf-text-muted)]">Loading customers…</div>
        ) : filteredCustomers.length === 0 ? (
          <div className="p-6 text-center text-[12.5px] text-[var(--sf-text-muted)]">No matches</div>
        ) : (
          filteredCustomers.map((c) => {
            const name = `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.name || c.email || `#${c.id}`
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => {
                  setCustomerOverride({ id: c.id, name })
                  setSubView(null)
                }}
                className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-[var(--sf-bg-page)] border-b border-[var(--sf-border-light)] last:border-b-0"
              >
                <div>
                  <div className="text-[13px] font-semibold text-[var(--sf-text-primary)]">{name}</div>
                  {c.email && <div className="text-[11px] text-[var(--sf-text-muted)]">{c.email}</div>}
                </div>
                {customerOverride?.id === c.id && (
                  <span className="text-[11px] font-semibold text-[var(--sf-blue-500)]">Selected</span>
                )}
              </button>
            )
          })
        )}
      </div>
    </div>
  )

  const servicePicker = (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <button
          type="button"
          onClick={() => setSubView(null)}
          className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-[var(--sf-text-secondary)] hover:text-[var(--sf-text-primary)]"
        >
          <ChevronLeft className="w-4 h-4" />
          Back
        </button>
        <div className="text-[14px] font-bold text-[var(--sf-text-primary)]">Edit service data</div>
      </div>
      <p className="text-[12px] text-[var(--sf-text-secondary)] mb-3">
        Pick the service, price and duration the <strong>duplicate</strong> should use. The source job stays unchanged.
      </p>
      <label className="block text-xs font-semibold text-[var(--sf-text-secondary)] mb-1">Service</label>
      <select
        value={serviceForm.service_id || serviceOverride?.id || ""}
        onChange={(e) => {
          const id = e.target.value
          const svc = services.find((s) => String(s.id) === String(id))
          if (svc) {
            setServiceForm({
              price: svc.price != null ? String(svc.price) : "",
              duration: svc.duration != null ? String(svc.duration) : (svc.estimated_duration != null ? String(svc.estimated_duration) : ""),
              service_id: svc.id,
              service_name: svc.name,
            })
          }
        }}
        className="w-full px-3 py-2 text-sm border border-[var(--sf-border-light)] rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-[var(--sf-blue-500)]/40 focus:border-[var(--sf-blue-500)] mb-3"
      >
        <option value="">{servicesLoading ? "Loading services…" : "Pick a service"}</option>
        {services.map((s) => (
          <option key={s.id} value={s.id}>{s.name}</option>
        ))}
      </select>
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <label className="block text-xs font-semibold text-[var(--sf-text-secondary)] mb-1">Price ($)</label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={serviceForm.price}
            onChange={(e) => setServiceForm((f) => ({ ...f, price: e.target.value }))}
            className="w-full px-3 py-2 text-sm border border-[var(--sf-border-light)] rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-[var(--sf-blue-500)]/40 focus:border-[var(--sf-blue-500)]"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-[var(--sf-text-secondary)] mb-1">Duration (min)</label>
          <input
            type="number"
            min="0"
            step="15"
            value={serviceForm.duration}
            onChange={(e) => setServiceForm((f) => ({ ...f, duration: e.target.value }))}
            className="w-full px-3 py-2 text-sm border border-[var(--sf-border-light)] rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-[var(--sf-blue-500)]/40 focus:border-[var(--sf-blue-500)]"
          />
        </div>
      </div>
      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={() => setSubView(null)}
          className="px-4 py-2 text-sm text-[var(--sf-text-secondary)] bg-white border border-[var(--sf-border-light)] rounded-md hover:bg-[var(--sf-bg-page)]"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={!serviceForm.service_id}
          onClick={() => {
            setServiceOverride({
              id: serviceForm.service_id,
              name: serviceForm.service_name,
              price: serviceForm.price,
              duration: serviceForm.duration,
            })
            setSubView(null)
          }}
          className="px-4 py-2 text-sm font-semibold text-white bg-[var(--sf-blue-500)] rounded-md hover:opacity-90 disabled:opacity-50"
        >
          Apply to duplicate
        </button>
      </div>
    </div>
  )

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[10000] p-4"
      onClick={() => (busy ? null : onClose?.())}
    >
      <div
        className="bg-white rounded-xl shadow-xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-lg font-bold text-[var(--sf-text-primary)] flex items-center gap-2">
            <CopyIcon className="w-4 h-4 text-[var(--sf-blue-500)]" />
            Duplicate job
          </h3>
          <button
            type="button"
            onClick={() => (busy ? null : onClose?.())}
            disabled={busy}
            className="text-[var(--sf-text-muted)] hover:text-[var(--sf-text-primary)] p-1 disabled:opacity-50"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {subView === "customer" ? (
          customerPicker
        ) : subView === "service" ? (
          servicePicker
        ) : (
          <>
            <p className="text-[12.5px] text-[var(--sf-text-secondary)] mb-4">
              The duplicate copies every detail of <strong>{sourceCustomerName}</strong>
              {job?.service_name ? ` (${job.service_name})` : ""} — service, address,
              cleaner, pricing, modifiers. Override customer or service below if the
              duplicate should be different.
            </p>

            {/* Override summary rows — show source values with Edit buttons;
                when overridden, show the override + Reset. Edits never touch
                the source job (issue #38 reopen x4). */}
            <div className="space-y-2 mb-4">
              <div className="flex items-center gap-2 px-3 py-2 border border-[var(--sf-border-light)] rounded-md bg-[var(--sf-bg-page)]">
                <UserCog className="w-4 h-4 text-[var(--sf-text-muted)] flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-[10.5px] font-semibold text-[var(--sf-text-muted)] uppercase tracking-wider">
                    Customer {customerOverride && <span className="text-[var(--sf-blue-500)]">· overridden</span>}
                  </div>
                  <div className="text-[13px] font-semibold text-[var(--sf-text-primary)] truncate">
                    {effectiveCustomerName}
                  </div>
                </div>
                {customerOverride && (
                  <button
                    type="button"
                    onClick={() => setCustomerOverride(null)}
                    title="Reset to source customer"
                    className="text-[var(--sf-text-muted)] hover:text-[var(--sf-text-primary)] p-1"
                  >
                    <RotateCcw className="w-4 h-4" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setSubView("customer")}
                  className="text-[12px] font-semibold text-[var(--sf-blue-500)] hover:underline"
                >
                  Edit
                </button>
              </div>

              <div className="flex items-center gap-2 px-3 py-2 border border-[var(--sf-border-light)] rounded-md bg-[var(--sf-bg-page)]">
                <Pencil className="w-4 h-4 text-[var(--sf-text-muted)] flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-[10.5px] font-semibold text-[var(--sf-text-muted)] uppercase tracking-wider">
                    Service {serviceOverride && <span className="text-[var(--sf-blue-500)]">· overridden</span>}
                  </div>
                  <div className="text-[13px] font-semibold text-[var(--sf-text-primary)] truncate">
                    {effectiveServiceLabel}
                  </div>
                </div>
                {serviceOverride && (
                  <button
                    type="button"
                    onClick={() => setServiceOverride(null)}
                    title="Reset to source service"
                    className="text-[var(--sf-text-muted)] hover:text-[var(--sf-text-primary)] p-1"
                  >
                    <RotateCcw className="w-4 h-4" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setSubView("service")}
                  className="text-[12px] font-semibold text-[var(--sf-blue-500)] hover:underline"
                >
                  Edit
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-[var(--sf-text-secondary)] mb-1">
                    New date
                  </label>
                  <input
                    type="date"
                    required
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-[var(--sf-border-light)] rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-[var(--sf-blue-500)]/40 focus:border-[var(--sf-blue-500)]"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[var(--sf-text-secondary)] mb-1">
                    New time
                  </label>
                  <input
                    type="time"
                    value={time}
                    onChange={(e) => setTime(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-[var(--sf-border-light)] rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-[var(--sf-blue-500)]/40 focus:border-[var(--sf-blue-500)]"
                  />
                </div>
              </div>

              {error && (
                <div className="text-xs text-red-600">{error}</div>
              )}

              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => (busy ? null : onClose?.())}
                  disabled={busy}
                  className="px-4 py-2 text-sm text-[var(--sf-text-secondary)] bg-white border border-[var(--sf-border-light)] rounded-md hover:bg-[var(--sf-bg-page)] disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={busy}
                  className="px-4 py-2 text-sm font-semibold text-white bg-[var(--sf-blue-500)] rounded-md hover:opacity-90 disabled:opacity-50"
                >
                  {busy ? "Duplicating…" : "Duplicate job"}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  )
}
